// Fallback storage that implements the Browser's Storage interface
// Ideally, that would be a private class, but there's no elegant way of doing that in TypeScript
// eslint-disable-next-line max-classes-per-file
class InMemoryStorage implements Storage {
  private storage: Record<string, string> = {};

  // We won't be calling those, no need to include them in coverage report, but add them
  // because they are part of the browser's `Storage` interface
  /* v8 ignore start */
  get length(): number {
    return Object.keys(this.storage).length;
  }

  key(index: number): string {
    return Object.keys(this.storage)[index];
  }
  /* v8 ignore end */

  clear(): void {
    this.storage = {};
  }

  getItem(key: string): string {
    return (this.storage?.[key] as string) ?? null;
  }

  removeItem(key: string): void {
    delete this.storage[key];
  }

  setItem(key: string, input: string): void {
    this.storage[key] = input;
  }
}

type StorageProvider = () => Storage;

type StoredItemWithTTL<T> = {
  expires: number;
  data: T;
};

function isStoredItemWithTTL<T>(item: unknown): item is StoredItemWithTTL<T> {
  return (
    item instanceof Object &&
    "expires" in item &&
    "data" in item &&
    typeof item.expires === "number"
  );
}

function isExpired<T>(item: StoredItemWithTTL<T> | unknown): boolean {
  if (isStoredItemWithTTL(item) && item.expires) {
    return Date.now() >= item.expires;
  }

  return false;
}

export class UniversalStorage {
  private readonly fallbackStorage = new InMemoryStorage();
  private readonly provider: Storage;

  constructor(storageProvider: StorageProvider = () => window.localStorage) {
    try {
      // Chrome throws error even during simple attempt to access local or session storage if they are disabled
      // So before setItem / removeItem test let's try setup provider first
      this.provider = storageProvider();
      // Next test storage
      // In case of local / session storage are not available it will throw error during setItem attempt
      if (!this.isAvailable()) {
        this.provider = this.fallbackStorage;
      }
    } catch {
      this.provider = this.fallbackStorage;
    }
  }

  private isAvailable(): boolean {
    // Modified https://mathiasbynens.be/notes/localstorage-pattern
    const uid = `__o${Date.now()}__`;
    try {
      this.provider.setItem(uid, uid);
      const result = this.provider.getItem(uid) == uid;
      this.provider.removeItem(uid);
      return result;
    } catch {
      return false;
    }
  }

  getItem<T>(key: string, raw = false): T | string | null {
    try {
      let value: string | T | null = this.provider.getItem(key);

      if (value === null || raw) {
        return value;
      }

      value = this.parseValue(value);

      if (isStoredItemWithTTL(value)) {
        return this.handleTTLItem(key, value) as T;
      }

      return value;
    } catch {
      return this.fallbackStorage.getItem(key);
    }
  }

  private parseValue<T>(value: string): T {
    try {
      return JSON.parse(value);
    } catch {
      return value as unknown as T;
    }
  }

  private handleTTLItem<T>(key: string, value: StoredItemWithTTL<T>): T | null {
    if (isExpired(value)) {
      this.removeItem(key);
      return null;
    }
    return value.data as T;
  }

  private setItemWithTTL<T>(
    key: string,
    input: T,
    timeToLiveMs: number,
  ): boolean {
    // Store the item with TTL
    const expires = Date.now() + timeToLiveMs;
    const value: StoredItemWithTTL<T> = { expires, data: input };
    return this.setItem(key, value);
  }

  setItem<T>(
    key: string,
    input: Record<string, T> | string,
    timeToLiveMs?: number,
  ): boolean {
    // If timeToLiveMs is provided, store the item with TTL
    if (timeToLiveMs) {
      return this.setItemWithTTL(key, input, timeToLiveMs);
    }

    // If input is an object, stringify it
    const value: string =
      input instanceof Object ? JSON.stringify(input) : input;

    // Try to set the item in the provider, if it fails, set it in the fallback storage
    try {
      this.provider.setItem(key, value);
    } catch {
      this.fallbackStorage.setItem(key, value);
    }
    return true;
  }

  removeItem(key: string): void {
    // Try to remove the item from the provider, if it fails, remove it from the fallback storage
    try {
      this.provider.removeItem(key);
    } catch {
      this.fallbackStorage.removeItem(key);
    }
  }

  clear(): void {
    // Try to clear the provider, if it fails, clear the fallback storage
    try {
      this.provider.clear();
    } catch {
      this.fallbackStorage.clear();
    }
  }
}
